// Pure helpers for the full-screen viewer (MediaViewer.tsx).

export type Size = { width: number; height: number };
export type Box = { left: number; top: number; width: number; height: number };

// The photo's size when it fits the screen whole (no cropping).
export function fitSize(natural: Size | null, screen: Size): Size {
  if (!natural || !natural.width || !natural.height) return screen;
  const scale = Math.min(screen.width / natural.width, screen.height / natural.height);
  return { width: natural.width * scale, height: natural.height * scale };
}

// How far a zoomed photo may move: never so far that an edge comes inside the screen.
export function clampPan(x: number, y: number, scale: number, fitted: Size, screen: Size) {
  const maxX = Math.max(0, (fitted.width * scale - screen.width) / 2);
  const maxY = Math.max(0, (fitted.height * scale - screen.height) / 2);
  return { x: Math.min(maxX, Math.max(-maxX, x)), y: Math.min(maxY, Math.max(-maxY, y)) };
}

// Zooming around a point (pinch centre, double-tap spot): the photo point
// under it stays under it. Points are relative to the screen centre.
export function zoomAround(point: { x: number; y: number }, from: { scale: number; x: number; y: number }, scale: number) {
  const ratio = scale / from.scale;
  return { scale, x: point.x - (point.x - from.x) * ratio, y: point.y - (point.y - from.y) * ratio };
}

// The transform that puts the full-screen photo exactly over the small one
// it was opened from (for the zoom in and out).
export function originTransform(origin: Box, fitted: Size, screen: Size): string {
  const scale = Math.max(origin.width / fitted.width, origin.height / fitted.height);
  const dx = origin.left + origin.width / 2 - screen.width / 2;
  const dy = origin.top + origin.height / 2 - screen.height / 2;
  return `translate(${dx}px, ${dy}px) scale(${scale})`;
}

const EXTENSIONS: Record<string, string> = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/gif': 'gif', 'image/avif': 'avif', 'video/mp4': 'mp4', 'video/webm': 'webm', 'video/quicktime': 'mov' };

// "guildite-efua-boateng-2026-10-30-1405.jpg": who sent it and when.
export function downloadName(item: { kind: 'photo' | 'video'; url: string; from?: { name: string } | null; at?: string | null }, type = ''): string {
  const who = (item.from?.name ?? '').normalize('NFKD').replace(/[^\w\s-]/g, '').trim().toLowerCase().replace(/[\s_]+/g, '-').slice(0, 40);
  const date = item.at ? new Date(item.at) : null;
  const pad = (value: number) => String(value).padStart(2, '0');
  const when = date && !Number.isNaN(date.getTime())
    ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}` : '';
  const fromUrl = /\.([a-z0-9]{2,5})(?:\?|$)/i.exec(item.url)?.[1]?.toLowerCase();
  const extension = EXTENSIONS[type.split(';')[0] ?? ''] ?? (fromUrl === 'jpeg' ? 'jpg' : fromUrl) ?? (item.kind === 'video' ? 'mp4' : 'jpg');
  return `${['guildite', who, when].filter(Boolean).join('-')}.${extension}`;
}

// Cloudinary sends the file as a download with this name (used when the
// browser can't fetch it itself).
export function attachmentUrl(url: string, name: string): string {
  const match = /\/(image|video)\/upload\//.exec(url);
  if (!url.includes('res.cloudinary.com') || !match) return url;
  const base = name.replace(/\.[a-z0-9]+$/i, '').replace(/[^\w-]/g, '_');
  return url.replace(match[0], `${match[0]}fl_attachment:${base}/`);
}
